import React from 'react';
import { Table, FormControl } from 'react-bootstrap';

import Translate from '../../../utils/Translate'

const periods = ["period_1", "period_2", "period_3", "period_4", "period_5", "period_6"];

const inputRows = [
	{ field: "revenues", label: "Revenues", sign: "positive" },
	{ field: "laborCosts", label: "Labor Costs", sign: "negative" },
	{ field: "materialPurchase", label: "Material Purchase", sign: "negative" },
	{ field: "qualityCosts", label: "Quality Costs", sign: "negative" },
	{ field: "newRegions", label: "New Regions", sign: "negative" },
	{ field: "salesDistributionCosts", label: "Sales/Distribution Costs", sign: "negative" },
	{ field: "overheads", label: "Overheads", sign: "negative" },
	{ field: "changeInReceivables", label: "Change in Receivables", sign: "variable" },
	{ field: "changeInSupplierCredit", label: "Change in Supplier Credit", sign: "variable" },
	{ field: "newProductDevelopment", label: "New Product Development", sign: "negative" },
	{ field: "newPlants", label: "New Plants", sign: "negative" },
	{ field: "newMachines", label: "New Machines", sign: "negative" },
	{ field: "assetsSold", label: "Assets Sold", sign: "positive" },
	{ field: "liabilities", label: "Liabilities", sign: "variable" },
	{ field: "interests", label: "Interests", sign: "negative" }
];

const signByField = inputRows.reduce((signs, row) => {
	signs[row.field] = row.sign;
	return signs;
}, { liquidityStart: "positive" });

const isNumberInput = (value) => {
	return value === "" || /^-?\d*\.?\d*$/.test(value);
}

const periodCellStyle = { width: 50, color: "#FFF", fontSize: 10, paddingRight: 3 };
const periodHeaderStyle = { ...periodCellStyle, textAlign: "center" };

const periodState = (cashFlow, period) => {
	return (cashFlow && cashFlow[period]) ? cashFlow[period] : {};
}

const numberValue = (cashFlow, period, field) => {
	const parsedValue = parseFloat(periodState(cashFlow, period)[field]);
	return (!isNaN(parsedValue)) ? parsedValue : 0;
}

class CashFlow extends React.Component {

	handleChange = (field, value, period) => {
		if (!isNumberInput(value)) {
			return;
		}

		const parsedValue = parseFloat(value);
		let normalizedValue = value;

		if (value !== "" && !isNaN(parsedValue)) {
			if (signByField[field] === "positive") {
				normalizedValue = Math.abs(parsedValue);
			} else if (signByField[field] === "negative") {
				normalizedValue = -Math.abs(parsedValue);
			}
		}

		this.props.inputChanged("cashFlow", field, normalizedValue, period)
	}

	calculatePeriod = (period, previousLiquidityEnd) => {
		const liquidityStart = (period === "period_1") ? numberValue(this.props.cashFlow, period, "liquidityStart") : previousLiquidityEnd;
		const operatingCashFlow = [
			"revenues",
			"laborCosts",
			"materialPurchase",
			"qualityCosts",
			"newRegions",
			"salesDistributionCosts",
			"overheads",
			"changeInReceivables",
			"changeInSupplierCredit"
		].reduce((total, field) => total + numberValue(this.props.cashFlow, period, field), 0);
		const investingCashFlow = [
			"newProductDevelopment",
			"newPlants",
			"newMachines",
			"assetsSold"
		].reduce((total, field) => total + numberValue(this.props.cashFlow, period, field), 0);
		const freeCashFlow = operatingCashFlow + investingCashFlow;
		const financingCashFlow = numberValue(this.props.cashFlow, period, "liabilities") + numberValue(this.props.cashFlow, period, "interests");
		const netCashFlow = freeCashFlow + financingCashFlow;
		const liquidityEnd = liquidityStart + netCashFlow;

		return {
			liquidityStart,
			operatingCashFlow,
			investingCashFlow,
			freeCashFlow,
			financingCashFlow,
			netCashFlow,
			liquidityEnd
		};
	}

	renderInputCell = (period, field) => {
		return (
			<div key={period} style={periodCellStyle}>
				<FormControl
					aria-label="Small"
					inputMode="decimal"
					size="sm"
					value={periodState(this.props.cashFlow, period)[field] || ""}
					onChange={(e) => this.handleChange(field, e.target.value, period)}
					disabled={!this.props.status}
				/>
			</div>
		);
	}

	renderCalculatedCell = (period, value) => {
		return (
			<div key={period} style={periodCellStyle}>
				<FormControl aria-label="Small" size="sm" value={value} disabled />
			</div>
		);
	}

	renderInputRow = (row) => {
		return (
			<tr key={row.field}>
				<td>
					<div className="bold"><Translate alias="Forms" word={row.label} /></div>
				</td>
				<td>
					<div style={{ display: "flex" }}>
						{periods.map((period) => this.renderInputCell(period, row.field))}
					</div>
				</td>
			</tr>
		);
	}

	renderCalculatedRow = (field, label, calculations) => {
		return (
			<tr className="autofillFieldBackground">
				<td>
					<div className="bold"><Translate alias="Forms" word={label} /></div>
				</td>
				<td>
					<div style={{ display: "flex" }}>
						{periods.map((period) => this.renderCalculatedCell(period, calculations[period][field]))}
					</div>
				</td>
			</tr>
		);
	}

	renderLiquidityStartRow = (calculations) => {
		return (
			<tr>
				<td>
					<div className="bold"><Translate alias="Forms" word="Liquidity Start of Year" /></div>
				</td>
				<td>
					<div style={{ display: "flex" }}>
						{periods.map((period) => {
							if (period === "period_1") {
								return this.renderInputCell(period, "liquidityStart");
							}

							return this.renderCalculatedCell(period, calculations[period].liquidityStart);
						})}
					</div>
				</td>
			</tr>
		);
	}

	render() {
		const calculations = {};
		let previousLiquidityEnd = 0;

		periods.forEach((period) => {
			calculations[period] = this.calculatePeriod(period, previousLiquidityEnd);
			previousLiquidityEnd = calculations[period].liquidityEnd;
		});

		return (
			<Table bordered>
				<tbody>
					<tr>
						<th></th>
						<th style={{ padding: 5 }}>
							<div style={{ display: "flex" }}>
								{periods.map((period, index) => (
									<div key={period} style={periodHeaderStyle}>
										<Translate alias="Forms" word={`Period ${index + 1}`} />
									</div>
								))}
							</div>
						</th>
					</tr>
					{this.renderLiquidityStartRow(calculations)}
					{inputRows.slice(0, 9).map(this.renderInputRow)}
					{this.renderCalculatedRow("operatingCashFlow", "Operating Cash Flow", calculations)}
					{inputRows.slice(9, 13).map(this.renderInputRow)}
					{this.renderCalculatedRow("investingCashFlow", "Investing Cash Flow", calculations)}
					{this.renderCalculatedRow("freeCashFlow", "Net Cash Flow", calculations)}
					{inputRows.slice(13, 15).map(this.renderInputRow)}
					{this.renderCalculatedRow("financingCashFlow", "Financing Cash Flow", calculations)}
					{this.renderCalculatedRow("netCashFlow", "Free Cash Flow", calculations)}
					{this.renderCalculatedRow("liquidityEnd", "Liquidity End of Year", calculations)}
				</tbody>
			</Table>
		);
	}

}

export default CashFlow;
